'use client';
import { ValidationError } from '@eliadmin/core/src/api/errors';

import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import {
  createReservation,
  fetchReservationServices,
  type ReservationCategory,
  type ReservationPerson,
  type ReservationService,
} from '@eliadmin/core/src/api/reservation/reservation-api';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Form,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { InputProps } from 'react-select';
import type { Control, FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';
import { useForm, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { ReservationNavigation } from './reservation-navigation';
import { ChooseService } from './steps/choose-service';
import { ChoosePerson } from './steps/choose-person';
import { ChooseDate } from './steps/choose-date';
import { ReservationContactForm } from './steps/reservation-contact-form';
import { ReservationFormFooter } from './reservation-form-footer';
import type { GeneralLayout } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { ReservationConfirmation } from './reservation-confirmation';

export interface ReservationStepProps {
  form: UseFormReturn<ReservationFormSchemaType>;
  people?: ReservationPerson[];
  services?: ReservationService[];
  selectedService?: ReservationService;
  selectedPerson?: ReservationPerson;
  categories?: ReservationCategory[];
  selectedCategoryId?: number;
  setSelectedCategoryId?: (categoryId: number) => void;
}

export type ReservationStepsProps = { id: number; key: string; label: string };

export const RESERVATION_STEPS = [
  { id: 1, key: 'SERVICE', label: 'Výber služby' },
  { id: 2, key: 'PERSON', label: 'Výber pracovníka' },
  { id: 3, key: 'CALENDAR', label: 'Výber termínu' },
  { id: 4, key: 'CONTACTS', label: 'Kontaktné údaje' },
] as ReservationStepsProps[];

const STEP_COMPONENTS: Record<string, React.FC<ReservationStepProps>> = {
  SERVICE: ChooseService,
  PERSON: ChoosePerson,
  CALENDAR: ChooseDate,
  CONTACTS: ReservationContactForm,
};

const reservationFormSchema = (VM: FormValidationMessages) =>
  z.object({
    name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    surname: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    phone: z.string().min(1, VM.PHONE_INVALID_VALIDATION_MESSAGE()),
    reserved_at: z
      .string({ error: VM.TERMIN_MUST_BE_SELECTED() })
      .min(1, VM.TERMIN_MUST_BE_SELECTED()),
    person_count: z.number().min(1, '1'),
    locked: z.boolean(),
    // reservation_category: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    reservation_person: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    reservation_service: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    return_url: z.string(),
    gdpr: z.boolean().refine(v => v === true, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    vop: z.boolean().refine(v => v === true, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
  });

export type ReservationFormSchemaType = z.infer<ReturnType<typeof reservationFormSchema>>;

type ReservationConfirmationData = {
  reservedAt: string;
  serviceName: string;
  serviceDurationMinutes: number;
};

const RESERVED_AT_SEARCH_PARAM = 'reserved_at';

export const ReservationForm = ({
  people,
  categories,
  generalLayout,
}: {
  people: ReservationPerson[];
  categories: ReservationCategory[];
  generalLayout: GeneralLayout['data'];
}) => {
  const topRef = useRef<HTMLDivElement | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number>(categories[0]?.id);
  const { t } = useTranslations();
  const [currentStep, setCurrentStep] = useState(1);
  const locale = useLocaleStore(s => s.currentLocale);
  const { formValidationMessages } = useFormValidationMessages(t);
  const [confirmation, setConfirmation] = useState<ReservationConfirmationData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ReservationFormSchemaType>({
    resolver: zodResolver(reservationFormSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      phone: '',
      reserved_at: undefined,
      person_count: 1,
      locked: true,
      // reservation_category: String(categories[0]?.id ?? ''),
      reservation_person: String(people.find(p => p.id > 0)?.id ?? ''),
      reservation_service: '',
      // vop: false,
      gdpr: false,
      return_url: `${process.env.NEXT_PUBLIC_APP_URL}${ROUTES.RESERVATION_CONFIRM()}`,
    },
  });

  useEffect(() => {
    const url = new URL(window.location.href);
    const reservedAt = url.searchParams.get(RESERVED_AT_SEARCH_PARAM);
    if (!reservedAt || Number.isNaN(new Date(reservedAt).getTime())) return;

    form.setValue('reserved_at', reservedAt, { shouldDirty: true, shouldValidate: false });
    url.searchParams.delete(RESERVED_AT_SEARCH_PARAM);
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }, [form]);

  const { data: servicesResp } = useQuery({
    queryKey: ['REFERENCE_SERVICE', locale, selectedCategoryId],
    queryFn: () => fetchReservationServices({ locale, query: { categoryId: selectedCategoryId } }),
    enabled: !!selectedCategoryId,
  });

  const services: ReservationService[] = useMemo(
    () => (servicesResp as any)?.data ?? [],
    [servicesResp],
  );

  const selectedServiceId = form.watch('reservation_service');
  const selectedService = useMemo(
    () => services.find(s => String(s.id) === String(selectedServiceId)),
    [selectedServiceId, services],
  );

  const selectedPersonId = form.watch('reservation_person');
  const selectedPerson = useMemo(
    () => people.find(p => String(p.id) === String(selectedPersonId)),
    [selectedPersonId, people],
  );

  const handleReservationSubmit = async (values: ReservationFormSchemaType) => {
    if (!selectedPerson?.id || !selectedService?.id) return;

    try {
      setIsSubmitting(true);
      await createReservation({
        locale,
        body: {
          ...values,
          reservation_person: { id: selectedPerson.id },
          reservation_service: { id: selectedService.id },
        },
      });
      setConfirmation({
        reservedAt: values.reserved_at,
        serviceName: selectedService.name,
        serviceDurationMinutes: Number(selectedService.duration ?? 0),
      });
    } catch (reservationError) {
if (reservationError instanceof ValidationError) { for (const [field, messages] of Object.entries(reservationError.fields)) form.setError(field as any, { type: 'server', message: messages.join('\n') }); }
setIsSubmitting(false);

      setIsSubmitting(false);
      return;
    }
  };

  const goToNextStep = () => {
    if (currentStep < RESERVATION_STEPS.length) setCurrentStep(prev => prev + 1);
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) setCurrentStep(prev => prev - 1);
  };

  const CurrentStepComponent = STEP_COMPONENTS[RESERVATION_STEPS[currentStep - 1].key];

  const scrollToTop = () => {
    const el = topRef.current;
    if (!el) return;

    const y = el.getBoundingClientRect().top + window.scrollY - 100;
    window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
  };

  // Smooth scroll to top when entering the form.
  useEffect(() => {
    scrollToTop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Smooth scroll to top when switching steps.
  useEffect(() => {
    scrollToTop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  if (confirmation) {
    return (
      <ReservationConfirmation
        generalLayout={generalLayout}
        reservation={{
          reservedAt: confirmation.reservedAt,
          serviceName: confirmation.serviceName,
          serviceDurationMinutes: confirmation.serviceDurationMinutes,
        }}
        locale={locale}
      />
    );
  }

  return (
    <Form {...form}>
        <CaptchaFormMessage errors={form.formState.errors} />
      <form
        onSubmit={form.handleSubmit(handleReservationSubmit)}
        className={cn(
          'flex w-full max-w-[792px] flex-col items-center justify-center gap-8 sm:gap-10 lg:gap-12',
          'ea-reservation-form',
        )}
      >
        <div ref={topRef} />
        <ReservationNavigation steps={RESERVATION_STEPS} currentStep={currentStep} />
        <CurrentStepComponent
          form={form}
          people={people}
          services={services}
          selectedService={selectedService}
          selectedPerson={selectedPerson}
          categories={categories}
          selectedCategoryId={selectedCategoryId}
          setSelectedCategoryId={setSelectedCategoryId}
        />
        <ReservationFormFooter
          currentStep={currentStep}
          goToNextStep={goToNextStep}
          goToPreviousStep={goToPreviousStep}
          RESERVATION_STEPS={RESERVATION_STEPS}
          form={form}
          isNextDisabled={
            currentStep === 1 ? !selectedCategoryId || !form.getValues().reservation_service : false
          }
          isSubmitting={isSubmitting}
        />
      </form>
    </Form>
  );
};

type FieldProps<TFormValues extends FieldValues> = {
  control: Control<TFormValues>;
  name: FieldPath<TFormValues>;
  placeholder?: string;
  label?: string | React.ReactNode;
  required?: boolean;
  type?:
    | InputProps['type']
    | 'textarea'
    | 'select'
    | 'radio'
    | 'checkbox'
    | 'custom-select-service';
  options?: { value: any; label: string }[];
  description?: string | React.ReactNode;
  isDisabled?: boolean;
  className?: { wrapper?: string };
  selectedService?: ReservationService;
  services?: ReservationService[];
};

export function Field<TFormValues extends FieldValues>(props: FieldProps<TFormValues>) {
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const { clearErrors, trigger } = useFormContext();

  const content = useMemo(() => {
    if (props.type === 'custom-select-service') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem
              required={props.required}
              className="h-14 w-full rounded-[8px] bg-white sm:h-[54px]"
            >
              {props.label && <FormLabel>{props.label}</FormLabel>}
              <FormControl>
                <Select
                  value={String(field.value ?? '')}
                  onValueChange={async value => {
                    field.onChange(value);
                    clearErrors(props.name as any);
                    await trigger(props.name as any);
                  }}
                  disabled={props.isDisabled}
                >
                  <SelectTrigger
                    innerTriggerClassName="pl-6 pr-4"
                    className="h-14 w-full rounded-[8px] border border-black/10 bg-white pl-0 pr-4 sm:h-[54px]"
                  >
                    <span className="text-sm font-medium sm:text-base">
                      {field.value && props.selectedService
                        ? `${props.selectedService.name} (${props.selectedService.duration} min)`
                        : 'Vyberte si službu'}
                    </span>
                  </SelectTrigger>

                  <SelectContent
                    sideOffset={4}
                    className={cn(
                      'flex max-h-[200px] w-full flex-col items-start justify-start gap-4 overflow-y-auto rounded-[8px] p-1 sm:max-h-[300px]',
                      'ea-reservation-form__select-service-content',
                    )}
                  >
                    {props.options?.map((o, i) => {
                      const currentService = props.services?.find(
                        s => String(s.id) === String(o.value),
                      );
                      const isSelected = String(props.selectedService?.id) === String(o.value);

                      return (
                        <SelectItem
                          key={o.value}
                          value={String(o.value)}
                          className={cn(
                            'mb-1 flex w-full flex-1 items-center justify-between rounded-[8px] text-black hover:bg-primary-foreground',
                            isSelected && '!bg-black !text-white hover:bg-black focus:bg-black/50',
                            props.options && i === props.options.length - 1 && 'mb-0',
                          )}
                        >
                          <div className="flex w-full flex-1 items-center justify-between">
                            <p className="text-sm font-normal sm:text-base">
                              {currentService?.name} ({currentService?.duration} min)
                            </p>
                            <span className="text-sm font-bold sm:text-base">
                              &nbsp;&nbsp;{currentService?.price_with_vat} {currency}
                            </span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </FormControl>
              {props.description && (
                <p className="ml-3 h-5 py-1 text-[12px] text-sm leading-4 text-muted-foreground">
                  {props.description}
                </p>
              )}
              <FormMessage className="w-full text-center text-base" />
            </FormItem>
          )}
        />
      );
    }

    if (props.type === 'select') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required} className="w-full p-[1px] sm:w-full">
              {props.label && <FormLabel>{props.label}</FormLabel>}
              <FormControl>
                <Select
                  value={String(field.value)}
                  onValueChange={async value => {
                    field.onChange(value);
                    clearErrors(props.name as any);
                    await trigger(props.name as any);
                  }}
                  disabled={props.isDisabled}
                >
                  <SelectTrigger className="h-[70px] w-full rounded-[4px] border border-border bg-transparent">
                    <div className="flex w-full items-center justify-between pl-4 pr-6 font-semibold sm:pr-12">
                      <SelectValue placeholder={props.placeholder} />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {props.options?.map(o => (
                      <SelectItem key={o.value} value={String(o.value)}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              {props.description && (
                <p className="ml-3 h-5 py-1 text-[12px] text-sm leading-4 text-muted-foreground">
                  {props.description}
                </p>
              )}
              <FormMessage className="w-full text-center text-base" />
            </FormItem>
          )}
        />
      );
    }

    if (props.type === 'radio') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required} className="flex flex-col gap-2 p-[1px]">
              {props.label && <FormLabel>{props.label}</FormLabel>}
              <FormControl>
                <RadioGroup
                  value={String(field.value)}
                  onValueChange={field.onChange}
                  disabled={props.isDisabled}
                  className="flex flex-row gap-4 pl-2"
                >
                  {props.options?.map(o => (
                    <div key={o.value} className="flex gap-2">
                      <RadioGroupItem id={`radiogroupitem-${o.value}`} value={String(o.value)} />
                      <Label className="flex items-center" htmlFor={`radiogroupitem-${o.value}`}>
                        <p className="text-sm font-semibold text-foreground">{o.label}</p>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </FormControl>
              {props.description && (
                <p className="ml-3 h-5 py-1 text-[12px] text-sm leading-4 text-muted-foreground">
                  {props.description}
                </p>
              )}
              <FormMessage className="w-full text-center text-base" />
            </FormItem>
          )}
        />
      );
    }

    if (props.type === 'checkbox') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required}>
              <FormControl>
                <div className="flex w-full flex-row items-center gap-2">
                  <Checkbox
                    {...(field as any)}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={props.isDisabled}
                    aria-disabled={props.isDisabled}
                  />
                  {props.label && <FormLabel>{props.label}</FormLabel>}
                </div>
              </FormControl>
              <FormMessage className="w-full text-center text-base" />
            </FormItem>
          )}
        />
      );
    }

    if (props.type === 'textarea') {
      return (
        <FormField
          control={props.control}
          name={props.name}
          render={({ field }) => (
            <FormItem required={props.required}>
              {props.label && <FormLabel>{props.label}</FormLabel>}
              <FormControl>
                <Textarea
                  placeholder={props.placeholder}
                  {...(field as any)}
                  className="min-h-[108px] w-full rounded-[8px] border border-black/10 bg-white px-6 py-4 text-sm font-medium text-black shadow-none placeholder:text-black/50 sm:text-base"
                />
              </FormControl>
              {props.description && (
                <p className="ml-3 h-5 py-1 text-[12px] text-sm leading-4 text-muted-foreground">
                  {props.description}
                </p>
              )}
              <FormMessage className="w-full text-center text-base" />
            </FormItem>
          )}
        />
      );
    }

    return (
      <FormField
        control={props.control}
        name={props.name}
        render={({ field }) => (
          <FormItem required={props.required} className="h-[52px] w-full rounded-[8px] sm:h-[54px]">
            {props.label && <FormLabel>{props.label}</FormLabel>}
            <FormControl>
              <Input
                placeholder={props.placeholder}
                type={props.type}
                {...(field as any)}
                disabled={props.isDisabled}
                aria-disabled={props.isDisabled}
                classNames={{
                  main: 'w-full h-[52px] sm:h-[54px] rounded-[8px] text-left bg-white px-6 text-sm sm:text-base font-medium placeholder:text-black/50 text-black border border-black/10',
                  wrapper: 'h-[52px] sm:h-[54px] rounded-[8px] bg-transparent px-0',
                }}
              />
            </FormControl>
            {props.description && (
              <p className="ml-3 h-5 py-1 text-[12px] text-sm leading-4 text-muted-foreground">
                {props.description}
              </p>
            )}
            <FormMessage className="w-full text-center text-base" />
          </FormItem>
        )}
      />
    );
  }, [
    clearErrors,
    props.control,
    props.description,
    props.isDisabled,
    props.required,
    props.label,
    props.name,
    props.options,
    props.placeholder,
    props.type,
    props.selectedService,
    props.services,
    trigger,
    currency,
  ]);

  return <div className={cn(props.className?.wrapper)}>{content}</div>;
}
